import { API } from "apps/labelstudio/src/providers/ApiProvider";
import { absoluteURL } from "apps/labelstudio/src/utils/helpers";
import { chunkedImportFile, IMPORT_CHUNK_THRESHOLD_BYTES, isTransientStatus, waitForOnline } from "./chunkedImport";

const csrfCookie = (): string => {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

/**
 * POST a multipart body with byte-level upload progress. ``fetch`` cannot
 * report upload progress, so the import goes through XMLHttpRequest; the
 * same cookies and Authorization header the API client uses are sent.
 * Resolves to the parsed JSON body; on a non-2xx status the result carries
 * ``error: true`` and ``response`` (the API's JSON) like ``API.invoke``.
 */
export const postMultipartWithProgress = (
  path: string,
  body: FormData,
  onProgress?: (loaded: number, total: number) => void,
): Promise<any> =>
  new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path.match(/^https?:/) ? path : absoluteURL(path), true);
    xhr.withCredentials = true;
    const headers = (API as any).commonHeaders ?? {};
    for (const [k, v] of Object.entries(headers)) {
      if (k.toLowerCase() === "content-type") continue;
      if (typeof v === "string" && v) xhr.setRequestHeader(k, v);
    }
    const csrf = csrfCookie();
    if (csrf && !headers["X-CSRFToken"]) xhr.setRequestHeader("X-CSRFToken", csrf);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    };
    xhr.onerror = () => resolve({ error: true, response: { detail: "Network error during upload" } });
    xhr.onabort = () => resolve({ error: true, response: { detail: "Upload cancelled" } });
    xhr.onload = () => {
      let json: any = null;
      try {
        json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        json = { detail: xhr.responseText?.slice(0, 300) || `HTTP ${xhr.status}` };
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(json ?? {});
      else resolve({ error: true, status: xhr.status, response: json ?? { detail: `HTTP ${xhr.status}` } });
    };
    xhr.send(body);
  });

const importPath = (projectId: number | string, query: Record<string, string>) => {
  const qs = new URLSearchParams(query).toString();
  const gateway = String((API as any).gateway ?? "/api").replace(/\/+$/, "");
  return `${gateway}/projects/${projectId}/import${qs ? `?${qs}` : ""}`;
};

// The server rejects a request whose files SUM past TASKS_MAX_FILE_SIZE
// (250MB) — the cap is on the batch, not the file. Selecting 100 clips of
// 30MB therefore failed outright: 3GB in one POST, and every file under
// the 50MB chunking threshold, so nothing took the resumable path either.
// Split the small files into batches that stay under the cap. 200MB leaves
// headroom for field data and multipart overhead.
const BATCH_MAX_BYTES = 200 * 1024 * 1024;
// Server also caps files per request (DATA_UPLOAD_MAX_NUMBER_FILES = 100).
const BATCH_MAX_FILES = 50;
// Large files upload concurrently rather than strictly one after another.
// Kept low deliberately: a contributor on a slow link gains nothing from
// eight parallel streams competing for the same bandwidth.
const CHUNKED_CONCURRENCY = 3;
// Waits, in seconds, before a small file is sent again after the link or
// the server failed.
const SMALL_FILE_RETRY_WAITS = [2, 5, 15, 30];

/** Run `worker` over `items`, at most `limit` at a time, preserving order. */
async function mapWithLimit<T, R>(items: T[], limit: number, worker: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await worker(items[i]);
    }
  });
  await Promise.all(runners);
  return out;
}

export type FileOutcome = { name: string; size: number; error?: string };

/**
 * Upload files to a project. Each file travels in ITS OWN request
 * (chunked + resumable above ``IMPORT_CHUNK_THRESHOLD_BYTES``, one
 * multipart POST below it) so that:
 *  - every file reports its own progress (``onFileProgress``),
 *  - a file that fails never drags down files that already landed:
 *    their ids are still handed to ``onFinish`` and only the failed
 *    file is reported through ``onFileError`` / ``onError``.
 * Non-file bodies (import by URL) go through the API client unchanged.
 */
export const importFiles = async ({
  files,
  body,
  project,
  onUploadStart,
  onUploadFinish,
  onFinish,
  onError,
  onFileProgress,
  onFileDone,
  onFileError,
  dontCommitToProject,
}: {
  files: { name: string }[];
  body: Record<string, any> | FormData;
  project: APIProject;
  onUploadStart?: (files: { name: string }[]) => void;
  onUploadFinish?: (files: { name: string }[]) => void;
  onFinish?: (response: any) => void;
  onError?: (response: any) => void;
  onFileProgress?: (file: File, fraction: number, loaded: number) => void;
  onFileDone?: (file: File) => void;
  onFileError?: (file: File, message: string) => void;
  dontCommitToProject?: boolean;
}) => {
  onUploadStart?.(files);

  const query = dontCommitToProject ? { commit_to_project: "false" } : {};

  if (!(body instanceof FormData)) {
    // Import by URL (or any non-file body): unchanged, one request.
    const res = await API.invoke(
      "importFiles",
      { pk: project.id, ...query },
      { headers: { "Content-Type": "application/x-www-form-urlencoded" }, body },
    );
    if (res && !res.error) await onFinish?.(res);
    else onError?.(res?.response);
    onUploadFinish?.(files);
    return;
  }

  const fileEntries: [string, File][] = [];
  const otherFields: [string, any][] = [];
  for (const [key, value] of body.entries()) {
    if (value instanceof File) fileEntries.push([key, value]);
    else otherFields.push([key, value]);
  }

  const ids: number[] = [];
  let couldBeTasksList = false;
  const dataColumns = new Set<string>();
  const failures: FileOutcome[] = [];
  const done: File[] = [];

  const errorMessage = (r: any, file: File): string => {
    const resp = r?.response ?? r;
    const msg =
      resp?.validation_errors?.non_field_errors?.[0] ??
      resp?.detail ??
      resp?.error ??
      (typeof resp === "string" ? resp : null) ??
      `Upload of ${file.name} failed`;
    return String(msg);
  };

  const uploadOne = async ([key, file]: [string, File]) => {
    onFileProgress?.(file, 0, 0);
    try {
      if (file.size >= IMPORT_CHUNK_THRESHOLD_BYTES) {
        const r = await chunkedImportFile(project.id, file, {
          commit: !dontCommitToProject,
          onProgress: (fraction) => onFileProgress?.(file, fraction, Math.round(fraction * file.size)),
        });
        if (r?.file_upload_id != null) ids.push(r.file_upload_id);
        else throw new Error(`Upload of ${file.name} returned no file id`);
        if (r.could_be_tasks_list) couldBeTasksList = true;
        for (const c of r.data_columns ?? []) dataColumns.add(c);
      } else {
        // A stored file that is not yet a task can be sent again safely, so
        // a dropped connection is retried here. A committing upload is not:
        // a request that landed with its answer lost would import twice.
        const waits = dontCommitToProject ? SMALL_FILE_RETRY_WAITS : [];
        let r: any = null;
        for (let attempt = 0; attempt <= waits.length; attempt++) {
          await waitForOnline();
          const fd = new FormData();
          for (const [k, v] of otherFields) fd.append(k, v);
          fd.append(key, file);
          r = await postMultipartWithProgress(importPath(project.id, query), fd, (loaded, total) =>
            onFileProgress?.(file, total ? loaded / total : 0, loaded),
          );
          if (r && !r.error) break;
          if (!isTransientStatus(r?.status) || attempt === waits.length) break;
          onFileProgress?.(file, 0, 0);
          await new Promise((resolve) => setTimeout(resolve, waits[attempt] * 1000));
        }
        if (!r || r.error) throw new Error(errorMessage(r, file));
        ids.push(...(r.file_upload_ids ?? []));
        if (r.could_be_tasks_list) couldBeTasksList = true;
        for (const c of r.data_columns ?? []) dataColumns.add(c);
      }
      onFileProgress?.(file, 1, file.size);
      done.push(file);
      onFileDone?.(file);
    } catch (e: any) {
      const message = e?.message || `Upload of ${file.name} failed`;
      failures.push({ name: file.name, size: file.size, error: message });
      onFileError?.(file, message);
    }
  };

  // A few at a time: enough to overlap small files, not so many that one
  // slow link starves them all.
  await mapWithLimit(fileEntries, Math.max(1, Math.min(CHUNKED_CONCURRENCY, 3)), uploadOne);

  if (ids.length) {
    await onFinish?.({
      file_upload_ids: ids,
      could_be_tasks_list: couldBeTasksList,
      data_columns: [...dataColumns],
      failed: failures,
    });
  }
  if (failures.length) {
    onError?.({
      detail:
        failures.length === 1
          ? failures[0].error
          : `${failures.length} files did not upload: ${failures.map((f) => f.name).join(", ")}`,
      failed: failures,
    });
  }
  onUploadFinish?.(files);
};
